import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useGetFamilyByStudent } from '~entities/family';
import { useIsStudioV2 } from '~entities/studios/hooks/use-is-studio-v2';

import { Button, type ButtonProps } from '~shared/shadcn/ui/button';

type FamilyHubLinkButtonProps = {
  studentId: string;
  parentId?: string | null;
  label: string;
  className?: string;
  variant?: ButtonProps['variant'];
  children?: ReactNode;
  onNavigate?: () => void;
};

/**
 * V2 → `/families/:familyId/summary`. V1 → classic `/family?parentId=`.
 * While V2 family lookup is in flight, stay disabled (do not fall through to
 * classic `/family` and bounce through the redirect).
 */
export const FamilyHubLinkButton = ({
  studentId,
  parentId,
  label,
  className,
  variant = 'secondary',
  children,
  onNavigate,
}: FamilyHubLinkButtonProps) => {
  const { isV2, isLoading: isV2Loading } = useIsStudioV2();
  const { data, isLoading: isFamilyLoading } = useGetFamilyByStudent(
    isV2 ? studentId : undefined,
  );
  const familyId = data?.family?._id;
  const labelContent = children ?? label;

  if (isV2Loading || (isV2 && isFamilyLoading)) {
    return (
      <Button variant={variant} className={className} disabled>
        {labelContent}
      </Button>
    );
  }

  if (isV2 && familyId) {
    return (
      <Button variant={variant} className={className} asChild>
        <Link
          to='/families/$familyId/summary'
          params={{ familyId }}
          onClick={onNavigate}
        >
          {labelContent}
        </Link>
      </Button>
    );
  }

  // V2 with no family yet: do not deep-link to classic /family (would prompt
  // Create). Hide until a family exists or user creates one elsewhere.
  if (isV2) {
    return null;
  }

  if (!parentId) return null;

  return (
    <Button variant={variant} className={className} asChild>
      <Link to='/family' search={{ parentId }} onClick={onNavigate}>
        {labelContent}
      </Link>
    </Button>
  );
};
